import { Box, Button, Divider, MenuItem, Stack, TextField, Typography } from '@mui/material'
import { useAtom } from 'jotai'
import { FC, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { LocationChangeTimeZone } from '../../../api/models'
import { currentLocationChangeAtom } from '../../../atoms'
import { Page } from '../../../components/Page'
import { VALID_TIME_ZONES } from '../../../constants'
import { AddressConfirmationDialog } from '../AddressConfirmationPage/AddressConfirmationDialog'
import { addressValidationPaths } from '../addressValidationPaths'

type LocationChangeTimeZoneForm = {
  timeZone: LocationChangeTimeZone
}

export const TimeZonePage: FC = () => {
  const navigate = useNavigate()

  const {
    handleSubmit,
    register,
    formState: { errors, isValid },
    watch,
    setValue,
    getValues,
    trigger
  } = useForm<LocationChangeTimeZoneForm>()

  const [currentLocationAtom, setCurrentLocationAtom] = useAtom(currentLocationChangeAtom)
  const [isConfirmTimezoneDialogOpen, setIsConfirmTimezoneDialogOpen] = useState(false)

  const onSubmit = async () => {
    setIsConfirmTimezoneDialogOpen(true)
  }

  const handleConfirmTimezone = () => {
    if (currentLocationAtom) {
      // Add timezone to location change atom.
      const currentLocationAddressWithTimezone = {
        ...currentLocationAtom.address,
        timeZone: watch('timeZone')
      }
      const updatedCurrentLocationAtom = {
        ...currentLocationAtom,
        address: currentLocationAddressWithTimezone
      }
      // @ts-expect-error - it's warning that street and country could be undefined which they won't be.
      // TODO: Consult with team to determine if there is a better way to clarify this.
      setCurrentLocationAtom(updatedCurrentLocationAtom)

      // Navigate to confirmation
      navigate(`../${addressValidationPaths.datePicker}`)
    }
  }

  return (
    <Page title={'Address Change Scheduler'}>
      <Stack sx={{ position: 'relative' }}>
        <Stack spacing={'var(--spacing-l)'} sx={{ width: '100%' }}>
          <Box
            sx={{
              bgcolor: 'var(--bg-surface)',
              borderRadius: 'var(--border-radius-l)',
              p: 'var(--spacing-m)',
              gap: 'var(--spacing-2xl)'
            }}
          >
            <Stack spacing={'var(--spacing-l)'}>
              <Typography variant="h2" fontSize={'var(--size-xl)'} fontWeight="500" color="text.primary">
                Schedule an Address Change
              </Typography>
              <Stack sx={{ marginTop: 'var(--spacing-2xl)', marginLeft: 'var(--spacing-m)' }} spacing={'var(--spacing-m)'}>
                <Typography variant="h3" fontSize={'var(--size-l)'} fontWeight="500" color="text.primary">
                  Verify Timezone
                </Typography>
                <Typography variant="body1" fontWeight="400" color="text.primary">
                  We couldn't verify the timezone for this address. Please review before continuing. This will affect the time shown to our monitoring
                  agents in an emergency.
                </Typography>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <Stack spacing={'var(--spacing-l)'}>
                    <TextField
                      {...register('timeZone', {
                        required: 'Timezone is required'
                      })}
                      slotProps={{
                        // @ts-expect-error - select doesn't expose the data attributes but this is valid
                        select: { ['data-testid']: 'timeZone' }
                      }}
                      select
                      value={watch('timeZone') || ''}
                      label="Time Zone"
                      error={!!errors.timeZone}
                      helperText={errors.timeZone?.message}
                      onChange={e => {
                        setValue('timeZone', e.target.value as LocationChangeTimeZone)
                        trigger()
                      }}
                    >
                      {VALID_TIME_ZONES.map(type => {
                        return (
                          <MenuItem key={type} data-testid="timeZone_value" value={type}>
                            {type}
                          </MenuItem>
                        )
                      })}
                    </TextField>
                    <Divider sx={{ borderColor: 'var(--bg-surface-low)' }} />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--spacing-m)' }}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          navigate(-1)
                        }}
                        data-testid={'cancel-time-zone-selection-button'}
                      >
                        Back to edit
                      </Button>
                      <Button disabled={!isValid} variant="contained" color="primary" type="submit" data-testid={'time-zone-confirm-button'}>
                        Continue
                      </Button>
                    </Box>
                  </Stack>
                </form>
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </Stack>
      <AddressConfirmationDialog
        isOpen={isConfirmTimezoneDialogOpen}
        onClose={() => {
          setIsConfirmTimezoneDialogOpen(false)
        }}
        onConfirm={handleConfirmTimezone}
        customerAddress={{
          street1: currentLocationAtom!.address?.street1,
          street2: currentLocationAtom!.address?.street2,
          city: currentLocationAtom!.address?.city,
          state: currentLocationAtom!.address?.state,
          zip: currentLocationAtom!.address?.postalCode,
          timeZone: getValues('timeZone')
        }}
      />
    </Page>
  )
}
